import React, {
  type CSSProperties,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { VariantProps, cva } from "class-variance-authority";
import Image from "@/components/Atoms/Image";
import styles from "./index.module.scss";

export type ShapeVariantProps = VariantProps<typeof ShapeVariants>;
const ShapeVariants = cva(`left-0 z-10`, {
  variants: {
    direction: {
      Inner: `translate-y-[-38%] md:translate-y-[-30%]`,
      outer: `translate-y-[-100%]`,
      hero: `translate-y-[46%] sm:translate-y-[42%] md:translate-y-[40%] lg:translate-y-[34%]`,
      merge: `translate-y-[-55%] md:translate-y-[-65%]`,
      heroSlider:
        "translate-y-[15%] lg:translate-y-[10%] xl:translate-y-[12%]",
    },
    invert: {
      true: "scale-[-1] translate-y-[-45%]",
      false: "",
    },
    invertX: {
      true: "scale-x-[-1]",
      false: "",
    },
    position: {
      top: "top-0",
      bottom: "bottom-0",
    },
  },
  compoundVariants: [
    {
      direction: "Inner",
      position: "bottom",
      class: "translate-y-[25%] md:translate-y-[10%]",
    },
    {
      direction: "outer",
      position: "bottom",
      class: "translate-y-[100%]",
    },
    {
      direction: "merge",
      position: "bottom",
      class: "translate-y-[50%] md:translate-y-[60%]",
    },
  ],
});

interface Props {
  shape: any;
  color: any;
  direction?: "Inner" | "outer" | "merge" | "hero" | "heroSlider";
  invert: boolean;
  icon?: any;
  iconPosition?: "default" | "top" | string;
  position: "bottom" | "top" | null | undefined;
  invertX?: boolean;
}

/** Dato enums often arrive TitleCase ("Top"); treat as anchored-to-edge placement */
function isIconPositionTop(
  iconPosition: string | null | undefined,
): boolean {
  return String(iconPosition ?? "")
    .toLowerCase()
    .trim()
    === "top";
}

export function ShapeTop({ shape, color }: Props): JSX.Element {
  const style = {
    "--shape": `url(${shape?.img?.url})`,
    "--color": color?.color.hex,
  } as React.CSSProperties;
  return (
    <div style={style as React.CSSProperties} className={styles.shapeTop}></div>
  );
}

/** Matches the containing block `.shape` uses (nearest non-static/transform/filter ancestor), not our own wrapper — avoids trapping the spacer in a zero-height box */
function containingBlockForAbsoluteDescendant(shapeEl: HTMLElement): HTMLElement {
  let el: HTMLElement | null = shapeEl.parentElement;
  for (; el != null && el !== document.documentElement; el = el.parentElement) {
    const s = window.getComputedStyle(el);
    if (
      s.position !== "static" ||
      (s.transform && s.transform !== "none") ||
      (s.filter && s.filter !== "none") ||
      (s.perspective && s.perspective !== "none")
    ) {
      return el;
    }
  }
  return document.documentElement;
}

/** Seam = top edge of spacer bbox (after transforms). Using bottom trapped the icon inside the spacer box. */
function computeCrossingPx(shapeEl: HTMLElement): number {
  const cb = containingBlockForAbsoluteDescendant(shapeEl);
  const cbRect = cb.getBoundingClientRect();
  const shapeRect = shapeEl.getBoundingClientRect();
  return shapeRect.top - cbRect.top;
}

export function Shape({
  shape,
  color,
  direction,
  icon,
  iconPosition = "default",
  position,
  invert,
  invertX,
}: Props): JSX.Element {
  const anchorIconOnSpacerEdge = isIconPositionTop(iconPosition);
  const shapeRef = useRef<HTMLDivElement>(null);
  const [iconCrossingPx, setIconCrossingPx] = useState<number | null>(null);

  const style = {
    "--shape": `url(${shape?.img?.url})`,
    "--color": color?.color.hex,
    "--width": `${shape?.img?.width}px`,
    "--height": `${shape?.img?.height}px`,
  } as React.CSSProperties;

  useLayoutEffect(() => {
    if (!anchorIconOnSpacerEdge || !icon?.[0]) {
      setIconCrossingPx(null);
      return;
    }
    const shapeEl = shapeRef.current;
    if (!shapeEl) return;

    const update = () => {
      const sh = shapeRef.current;
      if (!sh) return;
      setIconCrossingPx(computeCrossingPx(sh));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(shapeEl);

    window.addEventListener("resize", update);

    let mo: MutationObserver | undefined;
    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(update);
      mo.observe(shapeEl, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      mo?.disconnect();
    };
  }, [
    anchorIconOnSpacerEdge,
    icon,
    iconPosition,
    position,
    direction,
    invert,
    invertX,
    shape,
  ]);

  const iconTopWrapperStyle: CSSProperties | undefined =
    anchorIconOnSpacerEdge && iconCrossingPx != null
      ? {
          position: "absolute",
          left: "50%",
          top: iconCrossingPx,
          transform: "translate(-50%, -50%)",
          zIndex: 2,
          lineHeight: 0,
        }
      : undefined;

  const iconTopFallbackClass =
    anchorIconOnSpacerEdge &&
    iconCrossingPx == null &&
    (position === "bottom"
      ? "absolute left-1/2 z-[2] -translate-x-1/2 translate-y-1/2 bottom-0"
      : "absolute left-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 top-0");

  return (
    <div className="w-full">
      {icon?.[0] && !anchorIconOnSpacerEdge && (
        <Image
          className={`${styles.icon} ${
            position === "bottom" ? styles.iconBottom : ""
          } m-auto absolute left-0 right-0 z-[2]`}
          data={icon[1]?.img}
        />
      )}
      {icon?.[0] && anchorIconOnSpacerEdge && (
        <div
          className={iconTopFallbackClass || ""}
          style={iconTopWrapperStyle}
        >
          <Image className="m-auto block max-w-none" data={icon[1]?.img} />
        </div>
      )}
      <div
        ref={shapeRef}
        style={style as React.CSSProperties}
        className={`${ShapeVariants({
          direction,
          position,
          invert,
          invertX,
        })} ${styles.shape} `}
      ></div>
    </div>
  );
}
